import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { NgTemplateOutlet } from '@angular/common';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AiAssistantOwner } from '@cureskin/api-client-rn';
import { AiAssistantChatService } from '@services/ai-assistant-chat-service/ai-assistant-chat.service';
import {
  AiAssistantChatInput,
  AiAssistantErrorState,
  AiAssistantMessage,
} from '@services/ai-assistant-chat-service/ai-assistant-chat.types';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { PRIMARY_CHANNELS } from '@services/event-logger-service/event-logger.constant';
import { InputType, SingleSelectorService } from '@shared/single-selector/single-selector.service';
import { VOICE_INPUT_STATE, VoiceInputService, VoiceInputState } from '@services/voice-input-service';
import { appendTranscript, scrollTextFieldToEnd } from '@services/voice-input-service/text-field.utils';
import { VoiceMicInputComponent } from '@components/voice-mic-input/voice-mic-input.component';

interface AiChatSheetData {
  user: Table.User;
  initialMessage?: string;
}

@Component({
  selector: 'app-ai-assistant-chat-bottomsheet',
  templateUrl: './ai-assistant-chat-bottomsheet.component.html',
  imports: [NgTemplateOutlet, VoiceMicInputComponent],
  providers: [AiAssistantChatService, SingleSelectorService],
})
export class AiAssistantChatBottomsheetComponent implements OnInit, OnDestroy {
  private readonly data = inject<AiChatSheetData>(MAT_BOTTOM_SHEET_DATA);
  private readonly chatService = inject(AiAssistantChatService);
  private readonly bottomSheetRef = inject<MatBottomSheetRef<AiAssistantChatBottomsheetComponent>>(MatBottomSheetRef);
  private readonly eventLogger = inject(EventLoggerService);
  readonly commonUtilityHelper = inject(CommonUtilityHelper);
  private readonly singleSelectorService = inject(SingleSelectorService);
  private readonly connectionService = inject(ConnectionService);
  private readonly voiceInputService = inject(VoiceInputService);
  private readonly zone = inject(NgZone);

  private readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');
  private readonly msgInput = viewChild<ElementRef<HTMLTextAreaElement>>('msgInput');

  readonly allChats = signal<AiAssistantMessage[]>([]);
  readonly latestMessage = signal<AiAssistantMessage | null>(null);
  readonly showThinking = signal<boolean>(false);
  readonly loadingOldMessages = signal<boolean>(false);
  readonly initialLoading = signal<boolean>(false);
  readonly errorState = signal<AiAssistantErrorState>('none');
  /** Mirrors the uncontrolled textarea so the send button and mic tooltip can react to it. */
  readonly draft = signal<string>('');
  readonly voiceMicSupported = signal<boolean>(false);
  readonly voiceTooltipDismissed = signal<boolean>(false);
  readonly voiceState = signal<VoiceInputState>(VOICE_INPUT_STATE.IDLE);

  protected readonly AiAssistantOwner = AiAssistantOwner;

  readonly inputLocked = computed(() => this.showThinking() || this.errorState() === 'retryable');

  /** Mic is hidden whenever the input itself is locked (bot thinking / retryable error). */
  readonly isVoiceMicVisible = computed(() => this.voiceMicSupported() && !this.inputLocked());

  readonly showVoiceTooltip = computed(() =>
    this.isVoiceMicVisible() && !this.voiceTooltipDismissed() && !this.draft());

  readonly isVoiceListening = computed(() => this.voiceState() === VOICE_INPUT_STATE.LISTENING);

  readonly messageRows = computed(() => {
    const messages = this.allChats();
    const lastIndex = messages.length - 1;
    const errorState = this.errorState();
    return messages.map((chat, index) => {
      const isLatestUserTurn = index === lastIndex && chat?.owner === AiAssistantOwner.USER;
      const canRetry = isLatestUserTurn && errorState === 'retryable';
      const showStaleHint = isLatestUserTurn && errorState === 'stale';
      const isHistoricalError = index !== lastIndex && this.chatService.isMessageErrored(chat, messages[index + 1]);
      return {
        chat,
        options: this.getMessageOptions(chat, index === lastIndex),
        showErrorBubble: canRetry || showStaleHint || isHistoricalError,
        canRetry,
        showStaleHint,
      };
    });
  });

  readonly suggestions = computed<AiAssistantChatInput[]>(() =>
    (this.latestMessage()?.suggestions ?? []) as AiAssistantChatInput[]);

  private readonly subscriptions: Subscription[] = [];
  private previousChatsLength: number | null = null;
  private draftContainsVoice: boolean = false;

  constructor() {
    this.setupBackdropHandler();
  }

  ngOnInit(): void {
    void this.initialise();
  }

  private async initialise(): Promise<void> {
    this.initialLoading.set(true);
    this.voiceInputService.reset();
    void this.resolveVoiceMicSupport();
    this.setupSubscriptions();
    try {
      await this.chatService.loadChatHistory();
      await this.handleInitialMessage();
    } finally {
      this.finishInitialization();
    }
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ name: 'paid_ai_chat_bottomsheet_rendered' }), PRIMARY_CHANNELS);
  }

  ngOnDestroy(): void {
    this.chatService.stopBotPolling();
    this.voiceInputService.reset();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  async sendMessage(text: string): Promise<void> {
    const message = text.trim();
    // Clear before the empty guard so a whitespace-only draft doesn't linger in the box.
    this.clearInput();
    if (!message) return;
    if (this.draftContainsVoice) {
      this.voiceInputService.notifyMessageSentWithVoice(message);
      this.draftContainsVoice = false;
    }
    await this.chatService.sendMessage(message);
  }

  onDraftChange(value: string): void {
    this.draft.set(value);
    this.dismissVoiceTooltip();
  }

  dismissVoiceTooltip(): void {
    this.voiceTooltipDismissed.set(true);
  }

  onVoiceTranscript(transcript: string): void {
    if (!transcript) return;
    const input = this.msgInput()?.nativeElement;
    const next = appendTranscript(input?.value ?? this.draft(), transcript);
    if (input) {
      input.value = next;
      scrollTextFieldToEnd(input);
    }
    this.draft.set(next);
    this.draftContainsVoice = true;
    this.dismissVoiceTooltip();
  }

  onVoiceStateChange(state: VoiceInputState): void {
    this.voiceState.set(state);
    if (state === VOICE_INPUT_STATE.LISTENING) this.dismissVoiceTooltip();
  }

  private async resolveVoiceMicSupport(): Promise<void> {
    this.voiceMicSupported.set(await this.voiceInputService.isMicAvailable());
  }

  onErrorBubbleClick(row: { canRetry: boolean }): void {
    if (row.canRetry) void this.chatService.retryLatestMessage();
  }

  getLinkOptions(chat: AiAssistantMessage): AiAssistantChatInput[] {
    return (chat?.options ?? []).filter(option => option.action === 'ACTION') as AiAssistantChatInput[];
  }

  getTextOptions(chat: AiAssistantMessage): AiAssistantChatInput[] {
    return (chat?.options ?? []).filter(option => option.action !== 'ACTION') as AiAssistantChatInput[];
  }

  getMessageOptions(chat: AiAssistantMessage, isLatest: boolean): AiAssistantChatInput[] {
    const linkOptions = this.getLinkOptions(chat);
    return isLatest ? [...linkOptions, ...this.getTextOptions(chat)] : linkOptions;
  }

  async handleInputClick(input: AiAssistantChatInput, chat?: AiAssistantMessage): Promise<void> {
    if (!input) return;
    if (input.action === 'ACTION' && input.params?.actionName) {
      await this.singleSelectorService.openInAppAction(
        input as unknown as InputType, {}, undefined, chat?.objectId ?? '', 'AiAssistantMessage');
      if (this.isInAppNavigation(input)) this.closePopup();
      return;
    }
    if (!input.text) return;
    await this.sendMessage(input.text);
  }

  closePopup(): void {
    this.bottomSheetRef.dismiss();
  }

  onScroll(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) return;
    if (container.scrollTop === 0 && !this.loadingOldMessages()) {
      this.chatService.loadMoreMessages();
    }
  }

  convertTextToHtml(text: string): string {
    return this.commonUtilityHelper.convertTextToHtml(text);
  }

  private isInAppNavigation(input: AiAssistantChatInput): boolean {
    const url = input.params?.url;
    if (!url) return false;
    return url.startsWith('/') || url.startsWith(this.connectionService.getBaseUrl());
  }

  private setupBackdropHandler(): void {
    this.bottomSheetRef.backdropClick().pipe(take(1)).subscribe(() => {
      const input = this.msgInput()?.nativeElement;
      if (input) {
        input.disabled = true;
        input.blur();
      }
    });
  }

  private finishInitialization(): void {
    this.initialLoading.set(false);
    setTimeout(() => this.scrollToBottom(), 0);
  }

  private clearInput(): void {
    this.draft.set('');
    const input = this.msgInput()?.nativeElement;
    if (!input) return;
    input.blur();
    input.value = '';
  }

  private async handleInitialMessage(): Promise<void> {
    const initialMessage = this.data?.initialMessage?.trim();
    if (!initialMessage) {
      this.chatService.resumePollingIfAwaitingReply();
      this.scrollToBottom();
      return;
    }
    await this.chatService.sendMessage(initialMessage);
    delete this.data.initialMessage;
    this.scrollToBottom();
  }

  private setupSubscriptions(): void {
    this.subscriptions.push(
      this.chatService.allChats$.subscribe(chats => this.handleChatsUpdate(chats)),
      this.chatService.latestMessage$.subscribe(message => this.latestMessage.set(message)),
      this.chatService.showThinking$.subscribe(thinking => this.handleThinkingState(thinking)),
      this.chatService.loadingOldMessages$.subscribe(loading => this.loadingOldMessages.set(loading)),
      this.chatService.errorState$.subscribe(errorState => this.errorState.set(errorState)),
    );
  }

  private handleChatsUpdate(chats: AiAssistantMessage[]): void {
    this.allChats.set(chats);
    const grew = !this.previousChatsLength || chats.length > this.previousChatsLength;
    if (!this.loadingOldMessages() && chats.length > 0 && grew) {
      this.scrollToBottom();
    }
    this.previousChatsLength = chats.length;
  }

  private handleThinkingState(thinking: boolean): void {
    this.showThinking.set(thinking);
    if (thinking) this.msgInput()?.nativeElement.blur();
  }

  private scrollToBottom(): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.jumpToBottom();
        requestAnimationFrame(() => this.jumpToBottom());
      });
      setTimeout(() => this.jumpToBottom(), 200);
    });
  }

  private jumpToBottom(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
    this.rescrollWhenImagesLoad(container);
  }

  private rescrollWhenImagesLoad(container: HTMLElement): void {
    Array.from(container.querySelectorAll('img'))
      .filter(image => !image.complete)
      .forEach(image => {
        image.addEventListener('load', () => this.jumpToBottom(), { once: true });
        image.addEventListener('error', () => this.jumpToBottom(), { once: true });
      });
  }
}
